import { useState, useCallback, useImperativeHandle } from 'react';

import { SearchIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';

import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import { cn } from '@/lib/utils';
import { createYupResolver } from '@/util/forms/createYupResolver';

const formSchema = yup.object({ selectedAgents: yup.array().of(yup.string()) });

type FormValues = yup.InferType<typeof formSchema>;

export interface ConnectAIAgentFormHandle {
  submitSelectedAgents: () => void;
}

export const ConnectAIAgentForm = ({ ref, ..._ }) => {
  // Local state
  const [searchQuery, setSearchQuery] = useState('');

  // Custom hooks
  const { collectionId } = useParams();
  const { t } = useTranslation();
  const { brainsByLanguage, updateBrain } = useBrains();
  const { collection } = useCollections(collectionId);
  const form = useForm<FormValues>({
    resolver: createYupResolver<FormValues>(formSchema),
    defaultValues: { selectedAgents: [] },
  });

  // Local variables
  const languageBrains = brainsByLanguage(collection?.language);
  const availableBrains = languageBrains.filter(
    // Filter out brains that are already connected to the collection
    (brain) => brain.collection_id !== collectionId
  );
  const filteredAgents = availableBrains.filter((brain) =>
    brain.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await Promise.all(
          values.selectedAgents.map(async (brain_id) => {
            const brain = languageBrains?.find((brain) => brain.brain_id === brain_id);

            return updateBrain(
              { brain_id: brain.brain_id, collection_id: collection.collection_id },
              {
                onSuccess: ({ name }) => {
                  toast.success(t('collections.toast_messages.ai_agent_connected', { 0: name }));
                },
              }
            );
          })
        );

        // Reset form after successful submission
        form.reset();
        setSearchQuery('');
      } catch (e) {
        toast.error(e.message);
      }
    },
    [collection?.collection_id, languageBrains, t, updateBrain, form]
  );

  useImperativeHandle(ref, () => ({
    submitSelectedAgents: () => {
      const selectedAgents = form.getValues('selectedAgents');
      if (selectedAgents && selectedAgents.length > 0) {
        form.handleSubmit(onSubmit)();
      }
      setSearchQuery('');
    },
  }));

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
        <FormField
          control={form.control}
          name="selectedAgents"
          render={({ field }) => (
            <FormItem>
              <div className="mb-0 border-b bg-background">
                <SearchIcon className="absolute top-2.5 left-2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('ai_agents.search_ai_agent')}
                  className="border-none pl-8 text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="max-h-60 space-y-1 bg-background p-1">
                <ScrollArea className={cn({ 'h-44': filteredAgents.length > 0 })}>
                  {filteredAgents.map((agent) => (
                    <div key={agent.brain_id} className="flex items-start space-x-3 px-2 py-1.5">
                      <FormControl>
                        <Checkbox
                          id={`agent-${agent.brain_id}`}
                          checked={field.value.includes(agent.brain_id)}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...field.value, agent.brain_id]
                              : field.value.filter((id) => id !== agent.brain_id);
                            field.onChange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormLabel
                        htmlFor={`agent-${agent.brain_id}`}
                        className="w-full cursor-pointer text-left text-sm leading-none font-normal text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {agent.name}
                      </FormLabel>
                    </div>
                  ))}
                </ScrollArea>

                {filteredAgents.length === 0 && (
                  <div className="text-center text-sm text-muted-foreground">
                    {t('ai_agents.no_ai_agents')}
                  </div>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
